import { Component, type ErrorInfo, type ReactNode } from "react";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { ApiError } from "@/api/client";

/**
 * Root-level fallback UI for unrecoverable render-time crashes.
 *
 * Security task 18 §6.1 — operator copies `request_id` from this screen and
 * matches it against the structlog event `api.unhandled_exception` emitted
 * by the backend `_unhandled_exc` handler.
 *
 * Why a class component: React 19 does not ship a hook for this. Class with
 * `getDerivedStateFromError` + `componentDidCatch` is the documented API
 * and works with the current TanStack Router setup. We wrap the entire
 * router (in `App.tsx`) so any descendant throw lands here; a router-based
 * navigation fallback could itself re-throw, so the only recovery action
 * is a full reload.
 */

interface State {
  error: unknown;
}

interface Props {
  children: ReactNode;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    // Mirror to the browser console for ops + e2e debugging. The server-side
    // log is the authoritative record; this is just a local breadcrumb.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] render-time crash", error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.error === null) {
      return this.props.children;
    }
    return <ErrorFallback error={this.state.error} />;
  }
}

function ErrorFallback({ error }: { error: unknown }): React.ReactElement {
  const { t } = useTranslation();
  const requestId = error instanceof ApiError ? error.requestId : null;

  async function copyRequestId(): Promise<void> {
    if (!requestId) return;
    try {
      await navigator.clipboard.writeText(requestId);
      toast.success(t("errorBoundary.copied"));
    } catch {
      toast.error(t("errorBoundary.copyFailed"));
    }
  }

  function reload(): void {
    window.location.reload();
  }

  return (
    <main
      role="alert"
      aria-live="assertive"
      className="flex min-h-svh items-center justify-center bg-background px-6 py-16"
    >
      {/*
        Single subtle entrance; keyframe `eb-fade-in` is declared in the
        scoped <style> tag at the bottom of this file to avoid bleeding into
        global CSS for a one-shot fallback.
      */}
      <div
        className="
          w-full max-w-md
          [animation:eb-fade-in_320ms_ease-out_both]
          motion-reduce:[animation:none]
        "
      >
        <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
          {t("errorBoundary.eyebrow")}
        </p>
        <h1
          className="
            mt-3 text-3xl font-medium leading-[1.15] tracking-tight text-foreground
            [font-feature-settings:'ss01','cv11']
          "
        >
          {t("errorBoundary.title")}
        </h1>
        <p className="mt-3 max-w-prose text-sm leading-relaxed text-muted-foreground">
          {t("errorBoundary.body")}
        </p>

        <hr className="my-6 border-0 border-t" style={{ borderColor: "var(--rule-line)" }} />

        {requestId ? (
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              {t("errorBoundary.requestIdLabel")}
            </p>
            <div className="mt-2 flex items-center gap-2">
              <code
                className="
                  flex-1 truncate rounded-sm border bg-muted/40
                  px-3 py-2 font-mono text-sm text-foreground
                "
                style={{ borderColor: "var(--rule-line)" }}
                data-testid="error-boundary-request-id"
              >
                {requestId}
              </code>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={copyRequestId}
                aria-label={t("errorBoundary.copy")}
              >
                <Copy aria-hidden className="size-4" />
                <span className="ml-1.5">{t("errorBoundary.copy")}</span>
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{t("errorBoundary.requestIdHint")}</p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t("errorBoundary.noRequestId")}</p>
        )}

        <div className="mt-8">
          <Button type="button" onClick={reload}>
            {t("errorBoundary.reload")}
          </Button>
        </div>
      </div>

      <style>{`@keyframes eb-fade-in {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}`}</style>
    </main>
  );
}
